/**
 * Translation Helper Utilities
 *
 * Utilities for handling translations, including stripping the $PLEASE_TRANSLATE$ marker
 */

const TRANSLATE_MARKER = '$PLEASE_TRANSLATE$'

/**
 * Strips the $PLEASE_TRANSLATE$ marker from a translation string
 * This marker is used to indicate that a translation needs to be done
 *
 * @param text - The text that may contain the marker
 * @returns The text without the marker
 */
export function stripTranslateMarker(text: string): string {
  if (!text) return text

  if (text.startsWith(TRANSLATE_MARKER)) {
    return text.substring(TRANSLATE_MARKER.length).trim()
  }

  return text
}

/**
 * Checks if a translation needs to be translated (has the marker)
 *
 * @param text - The text to check
 * @returns True if the text has the $PLEASE_TRANSLATE$ marker
 */
export function needsTranslation(text: string): boolean {
  return text?.startsWith(TRANSLATE_MARKER) ?? false
}

/**
 * Processes escape sequences in translation strings
 * Converts escape sequences like \n, \t, \\, \r to their actual characters
 *
 * @param text - The text that may contain escape sequences
 * @returns The text with escape sequences converted to actual characters
 *
 * @example
 * processEscapeSequences("Line 1\\nLine 2") // Returns "Line 1\nLine 2"
 * processEscapeSequences("Tab\\tSeparated") // Returns "Tab\tSeparated"
 */
export function processEscapeSequences(text: string): string {
  if (!text || typeof text !== 'string') {
    return text
  }

  return text
    .replace(/\\n/g, '\n') // \n → newline
    .replace(/\\t/g, '\t') // \t → tab
    .replace(/\\r/g, '\r') // \r → carriage return
    .replace(/\\\\/g, '\\') // \\ → backslash (must be last to avoid double processing)
}

/**
 * Recursively processes translation values to strip markers and process escape sequences
 * Handles strings, arrays, and objects
 *
 * @param value - The value to process (can be string, array, object, or primitive)
 * @returns The processed value with markers stripped and escape sequences processed
 *
 * @example
 * processTranslationValue("$PLEASE_TRANSLATE$Hello\\nWorld") // Returns "Hello\nWorld"
 * processTranslationValue(["$PLEASE_TRANSLATE$Item1", "Item2\\nLine2"]) // Returns ["Item1", "Item2\nLine2"]
 * processTranslationValue({ key: "$PLEASE_TRANSLATE$Value" }) // Returns { key: "Value" }
 */
export function processTranslationValue(value: unknown): unknown {
  // Handle strings - strip markers and process escape sequences
  if (typeof value === 'string') {
    let cleaned = stripTranslateMarker(value)
    cleaned = processEscapeSequences(cleaned)
    return cleaned
  }

  // Handle arrays - process each element recursively
  if (Array.isArray(value)) {
    return value.map((item) => processTranslationValue(item))
  }

  // Handle objects - process each value recursively
  if (value && typeof value === 'object') {
    const processed: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(value)) {
      processed[key] = processTranslationValue(val)
    }
    return processed
  }

  // For other types (numbers, booleans, null, undefined), return as-is
  return value
}

/**
 * Custom hook that extends useTranslation to automatically strip $PLEASE_TRANSLATE$ markers
 * Usage: const { t } = useTranslationWithMarkerStrip()
 */
import { useTranslation, UseTranslationResponse } from 'react-i18next'

export function useTranslationWithMarkerStrip(): UseTranslationResponse<'translation', undefined> {
  const translation = useTranslation()

  // Wrap the t function to strip markers and process escape sequences
  const originalT = translation.t
  const wrappedT = ((...args: unknown[]) => {
    const result = originalT(...(args as Parameters<typeof originalT>))

    if (typeof result === 'string') {
      // First strip the marker, then process escape sequences
      const cleaned = stripTranslateMarker(result)
      return processEscapeSequences(cleaned)
    }

    return result
  }) as unknown as typeof originalT

  return {
    ...translation,
    t: wrappedT
  }
}
